/**
 * Utilities for detecting and recovering from stale JS chunk errors.
 *
 * When Vite deploys a new build, content-hash chunk filenames change. Any
 * browser session that loaded the app before the deploy will try to fetch
 * old chunk URLs, which no longer exist on the CDN. This produces a
 * "Failed to fetch dynamically imported module" TypeError that bubbles up
 * to the error boundary.
 *
 * The correct recovery is a single hard page reload: the browser fetches
 * fresh HTML that references the new chunk hashes. A sessionStorage guard
 * prevents an infinite loop if the chunk is still missing after the reload
 * (e.g., a genuinely broken deploy).
 */

export const STALE_CHUNK_RELOAD_KEY = 'app_chunk_reload_attempted';

/**
 * Returns true when an error message string looks like a stale-chunk failure.
 * Exported so both the error boundary and the PostHog before_capture hook can
 * use the same detection logic without duplicating the patterns.
 */
export function isStaleChunkMessage(message: string): boolean {
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Importing a module script failed') ||
    message.includes('error loading dynamically imported module') ||
    // Mobile Safari: chunk URL returns an HTML 404 page instead of JS.
    message.includes('is not a valid JavaScript MIME type') ||
    // Safari bare "Load failed" when a dynamic import 404s.
    message === 'Load failed'
  );
}

/**
 * Returns true when the error looks like a stale dynamically-imported chunk.
 * Covers Chrome, Firefox, and Safari error message variants.
 */
export function isStaleChunkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return isStaleChunkMessage(error.message);
}

/**
 * If the error is a stale-chunk error and we have not already reloaded once
 * this session, sets the reload guard and reloads the page. Returns true when
 * a reload was triggered (caller should render null and wait for the reload).
 */
export function handleStaleChunkError(error: unknown): boolean {
  if (!isStaleChunkError(error)) return false;
  const alreadyRetried = sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY);
  if (alreadyRetried) return false;
  sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, '1');
  window.location.reload();
  return true;
}
